import React from 'react';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { adopt } from 'react-adopt';
import {
  path,
  append,
  reject,
  whereEq,
  compose,
  set,
  lensPath,
  __,
} from 'ramda';

const permissionPath = lensPath(['role', 'permissions']);

const addPerm = (permission, templatedRole) => compose(
  set(permissionPath, __, templatedRole),
  append(permission),
  path(['role', 'permissions']),
)(templatedRole);

const removePerm = (permission, templatedRole) => compose(
  set(permissionPath, __, templatedRole),
  reject(
    whereEq({ action: permission.action, resource: permission.resource }),
  ),
  path(['role', 'permissions']),
)(templatedRole);

export const GET_ALL_ROLES = gql`
{
  allRoles {
    roleIdentifier {
      name
      team
      displayName
    },
    permissions {
      action
      resource
      allowed
    }
  }
}
`;

export const GET_TEAM_ROLES = gql`
query($teamID: String!) {
  allTeamRoles(teamID: $teamID) {
    roleIdentifier{
      name
      team
      displayName
    },
    permissions {
      action
      resource
      allowed
    }
  }
}
`;

export const GET_ROLE = gql`
query($name:String!, $team:String!, $currentName: String, $currentTeam: String) {
  templatedRole(name: $name, team: $team, currentName: $currentName, currentTeam: $currentTeam) {
    role {
      roleIdentifier {
        name
        team
        displayName
      }
      permissions {
        action
        resource
        allowed
      }
    }
    template {
      resource
      description
      model
      actions {
        action
        description
      }
    }
    currentRole {
      roleIdentifier {
        name
        team
        displayName
      }
      permissions {
        action
        resource
        allowed
      }
    }
  }
}
`;


const updateCache = (cache, permission, name, team, updateFn, currentRole) => {
  const { templatedRole } = cache.readQuery({
    query: GET_ROLE,
    variables: {
      name, team, currentName: currentRole, currentTeam: team,
    },
  });
  cache.writeQuery({
    query: GET_ROLE,
    variables: {
      name, team, currentName: currentRole, currentTeam: team,
    },
    data: { templatedRole: updateFn(permission, templatedRole) },
  });
};

const SET_PERMISSION = gql`
mutation($action:String!, $resource:String!, $roleIdentifier:InputRoleIdentifier!) {
  allow(action:$action, resource:$resource, roleIdentifier:$roleIdentifier) {
    action
    resource
    allowed
  }
}
`;

const allow = ({
  render, roleID, teamID, currentRole,
}) => (
  <Mutation
    mutation={SET_PERMISSION}
    update={(cache, { data: { allow } }) => updateCache(cache, allow, roleID, teamID, addPerm, currentRole)}
  >
    {(mutation, result) => render({ mutation, result })}
  </Mutation>
);

const REMOVE_PERMISSION = gql`
mutation($action:String!, $resource:String!, $roleIdentifier:InputRoleIdentifier!) {
  removePermission(action:$action, resource:$resource, roleIdentifier:$roleIdentifier) {
    action
    resource
    allowed
  }
}
`;

const removePermission = ({
  render, roleID, teamID, currentRole,
}) => (
  <Mutation
    mutation={REMOVE_PERMISSION}
    update={(cache, { data: { removePermission } }) => updateCache(cache, removePermission, roleID, teamID, removePerm, currentRole)}
  >
    {(mutation, result) => render({ mutation, result })}
  </Mutation>
);

const CREATE_EMPTY_ROLE = gql`
mutation($roleIdentifier:InputRoleIdentifier!) {
  createEmptyRole(roleIdentifier:$roleIdentifier) {
    roleIdentifier {
      name
      displayName
      team
    }
  }
}
`;

const createEmptyRole = ({ render }) => (
  <Mutation
    mutation={CREATE_EMPTY_ROLE}
    context={{ useLocalError: true }}
  >
    {(mutation, result) => render({ mutation, result })}
  </Mutation>
);

export const RolesContainer = adopt({
  allow,
  removePermission,
  createEmptyRole,
});
